import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {Link} from 'react-router-dom';

const text = (value: unknown) => typeof value === 'string' ? value.trim() : '';
const photo = (value: unknown) => /^https?:\/\//i.test(text(value)) ? text(value) : undefined;
function date(value: unknown, withTime = false) {
  if (!text(value)) return 'Not provided';
  const parsed = new Date(text(value));
  if (!Number.isFinite(parsed.getTime())) return 'Not provided';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: withTime ? 'Africa/Lagos' : 'UTC', day: 'numeric', month: 'short', year: 'numeric',
    ...(withTime ? {hour: '2-digit', minute: '2-digit', hour12: true} as const : {}),
  }).format(parsed);
}

export default function IDCheckAccount({user}: {user: any}) {
  const accountId = text(user?._id) || text(user?.id);
  const isProvider = ['freelancer', 'contractor'].includes(user?.accountType);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setProfile(null); setError(''); setLoading(false);
    if (!accountId || !isProvider) return;
    setLoading(true);
    axios.get(`${process.env.REACT_APP_BASE_URL || 'https://api.pureworker.com/api'}/admin/providers`, {
      params: {_id: accountId, limit: 1}, signal: controller.signal, withCredentials: true, timeout: 25000,
    }).then(response => {
      if (controller.signal.aborted) return;
      const account = response.data?.providers?.results?.find((row: any) => String(row._id || row.id) === accountId);
      if (!account) throw new Error('Account not found');
      setProfile(account);
    }).catch(() => {
      if (!controller.signal.aborted) setError('Additional profile details could not be loaded. The basic account details are still shown below.');
    }).finally(() => {if (!controller.signal.aborted) setLoading(false);});
    return () => controller.abort();
  }, [accountId, isProvider]);

  // Never show another account's details while the route or request is changing.
  const account = profile && String(profile._id || profile.id) === accountId ? {...user, ...profile} : user;
  const name = [text(account?.firstName), text(account?.lastName)].filter(Boolean).join(' ') || text(account?.fullName) || text(account?.businessName) || 'Name not provided';
  const services = Array.isArray(account?.services) ? account.services.map((service: any) => text(service?.name)).filter(Boolean).join(', ') : '';
  const fields = [
    ['Name', name], ['Business name', text(account?.businessName)],
    ['Email', text(account?.email)], ['Phone number', text(account?.phoneNumber)],
    ['Date of birth', date(account?.dob)], ['Account type', text(account?.accountType)],
    ['State', text(account?.state)], ['Address', text(account?.address)],
    ['Services', services], ['Registered on (Lagos time)', date(account?.createdAt, true)],
  ];
  return <section aria-labelledby="id-check-account-heading" className="m-4 rounded-lg border p-4 sm:p-6" style={{background: 'var(--admin-surface)'}}>
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div>
        <h2 id="id-check-account-heading" className="text-lg font-semibold">Person being checked</h2>
        <p className="text-sm">Details from their Pureworker account</p>
      </div>
      {accountId && isProvider && <Link className="underline" to={`/provider/${encodeURIComponent(accountId)}`}>View full profile</Link>}
    </div>
    {!accountId ? <p>Account details are unavailable for this submission. Return to ID Check and refresh.</p> : <>
      {loading && <p role="status" className="mb-3 text-sm">Loading additional profile details...</p>}
      {error && <p role="status" className="mb-3 text-sm">{error}</p>}
      <div className="flex flex-wrap gap-5">
        {[[photo(account?.profilePic), 'Profile photo'], [photo(account?.verificationImage), 'Submitted selfie']].map(([src, label]) => src && <figure key={label}>
          <img src={src} alt={label} className="h-28 w-28 rounded-lg object-cover" />
          <figcaption className="mt-1 text-sm">{label}</figcaption>
        </figure>)}
        <dl className="grid w-full lg:flex-1 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 min-w-0">
          {fields.map(([label, value]) => <div key={label} className="min-w-0">
            <dt className="text-sm" style={{color: 'var(--admin-muted)'}}>{label}</dt>
            <dd className="mt-1 break-words">{value || 'Not provided'}</dd>
          </div>)}
        </dl>
      </div>
    </>}
  </section>;
}
